import { doc, getDoc, updateDoc, collection, addDoc, query, where, orderBy, getDocs, onSnapshot } from "firebase/firestore"
import { db } from "./config"
import { sendEmailNotificationWithRetry, sendEmailNotificationViaContact, sendEmailViaFirebaseFunction } from "./emailNotifications"

// Add a new notification
export const addNotification = async (notificationData) => {
    try {
        const notificationsRef = collection(db, "notifications")
        const docRef = await addDoc(notificationsRef, {
            ...notificationData,
            timestamp: new Date(),
            isRead: false
        })
        console.log("Notification added with ID: ", docRef.id)
        
        // Send email notification in background (don't wait for it)
        console.log('=== TRIGGERING EMAIL NOTIFICATION ===');
        console.log('Recipient ID:', notificationData.recipientId);
        console.log('Notification type:', notificationData.type);
        
        // Try Firebase Function approach (most reliable)
        sendEmailViaFirebaseFunction(notificationData.recipientId, notificationData)
            .then(success => {
                if (success) {
                    console.log('✅ Email notification sent successfully via Firebase Function');
                } else {
                    console.log('❌ Email notification failed to send via Firebase Function');
                    // Fallback to Contact template approach
                    return sendEmailNotificationViaContact(notificationData.recipientId, notificationData);
                }
            })
            .then(success => {
                if (success) {
                    console.log('✅ Email notification sent successfully (Contact template fallback)');
                } else {
                    console.log('❌ Email notification failed to send (both methods)');
                }
            })
            .catch(error => {
                console.error('❌ Error sending email notification:', error);
            });
        
        return docRef
    } catch (error) {
        console.error("Error adding notification: ", error)
        throw error
    }
}

// Get unread notifications for a user
export const getUnreadNotifications = async (userId) => {
    try {
        console.log('Getting unread notifications for user:', userId);
        const notificationsRef = collection(db, "notifications");
        
        // First try the simple query without orderBy
        const q = query(
            notificationsRef,
            where("recipientId", "==", userId),
            where("isRead", "==", false)
        );
        
        const querySnapshot = await getDocs(q);
        const notifications = [];
        
        querySnapshot.forEach((doc) => {
            notifications.push({ id: doc.id, ...doc.data() });
        });
        
        // Sort in JavaScript instead of Firestore
        notifications.sort((a, b) => {
            const aTime = a.timestamp?.toDate?.() || new Date(a.timestamp);
            const bTime = b.timestamp?.toDate?.() || new Date(b.timestamp);
            return bTime - aTime; // Descending order
        });
        
        console.log('Unread notifications found:', notifications.length);
        return notifications;
    } catch (error) {
        console.error("Error getting unread notifications:", error);
        return [];
    }
}

// Mark notification as read
export const markNotificationAsRead = async (notificationId) => {
    try {
        const notificationRef = doc(db, "notifications", notificationId);
        await updateDoc(notificationRef, { isRead: true });
        console.log("Notification marked as read:", notificationId);
    } catch (error) {
        console.error("Error marking notification as read:", error);
        throw error;
    }
}

// Real-time listener for unread notifications
export const listenToUnreadNotifications = (userId, callback) => {
    try {
        console.log('=== SETTING UP REAL-TIME NOTIFICATIONS LISTENER ===');
        console.log('User ID:', userId);
        
        const notificationsRef = collection(db, "notifications");
        
        const unreadNotificationsQuery = query(
            notificationsRef,
            where("recipientId", "==", userId),
            where("isRead", "==", false)
        );
        
        console.log('Setting up listener for unread notifications...');
        
        const unsubscribe = onSnapshot(unreadNotificationsQuery, (snapshot) => {
            try {
                console.log('=== UNREAD NOTIFICATIONS LISTENER TRIGGERED ===');
                console.log('Total unread notifications:', snapshot.size);
                
                const notifications = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
                console.log('Unread notifications:', notifications);
                
                // Sort in JavaScript
                notifications.sort((a, b) => {
                    const aTime = a.timestamp?.toDate?.() || new Date(a.timestamp);
                    const bTime = b.timestamp?.toDate?.() || new Date(b.timestamp);
                    return bTime - aTime; // Descending order
                });
                
                console.log('Calling callback with notifications...');
                
                if (Array.isArray(notifications)) {
                    callback(notifications);
                } else {
                    console.error('Invalid notifications:', notifications);
                    callback([]);
                }
            } catch (error) {
                console.error('Error processing notifications:', error);
                callback([]);
            }
        }, (error) => {
            console.error("Error in notifications listener:", error);
            callback([]);
        });
        
        return () => {
            console.log('=== CLEANING UP NOTIFICATIONS LISTENER ===');
            unsubscribe();
        };
        
    } catch (error) {
        console.error("Error setting up notifications listener:", error);
        return () => {};
    }
}

// Create admin notification for listing approval/decline
export const createAdminNotification = async (listingId, action, userId) => {
    try {
        console.log('Creating admin notification:', { listingId, action, userId });
        
        const notificationData = {
            type: 'listing_status',
            action: action, // 'approved' or 'declined'
            listingId: listingId,
            recipientId: userId,
            message: `Your listing has been ${action}.`,
            timestamp: new Date(),
            isRead: false
        };
        
        const result = await addNotification(notificationData);
        console.log('Admin notification created:', result);
        return result;
    } catch (error) {
        console.error('Error creating admin notification:', error);
        throw error;
    }
}

// Test function to add a notification
export const testAddNotification = async (userId) => {
    try {
        console.log('=== TESTING NOTIFICATION ADDITION ===');
        const testNotification = {
            type: 'listing_status',
            action: 'approved',
            listingId: 'test-listing-' + Date.now(),
            recipientId: userId,
            message: 'Test notification for real-time listener - ' + new Date().toLocaleTimeString(),
            timestamp: new Date(),
            isRead: false
        };
        
        const result = await addNotification(testNotification);
        console.log('Test notification added:', result);
        return result;
    } catch (error) {
        console.error('Error adding test notification:', error);
        throw error;
    }
} 